import type {
  CompanyCandidate,
  CompanyCandidatesResponse,
  CompanyNameSuggestion,
  CompanyProfile,
  CompanyReport,
  ResearchCompanyRequest,
} from "@scoach/types";
import { api } from "../../lib/http.ts";

/**
 * Company research + autocomplete. Shared by the regular meeting setup page and
 * the Quick-live modal. Profiles are cached server-side (shared across reps), so
 * `research()` is cheap on a cache hit.
 */
export const companiesApi = {
  research: (body: ResearchCompanyRequest) =>
    api<CompanyProfile>("/companies/research", { method: "POST", body }),

  list: () => api<{ items: CompanyNameSuggestion[] }>("/companies").then((r) => r.items),

  /** Fast name→company+logo typeahead (Brandfetch). Used by the company picker so
   * the user chooses the right company (with its logo) before research runs. */
  brandSearch: (q: string): Promise<CompanyCandidate[]> =>
    api<CompanyCandidatesResponse>("/companies/brand-search", { query: { q } }).then(
      (r) => r.candidates,
    ),

  get: (slug: string) => api<CompanyProfile>(`/companies/${slug}`),

  /** Meeting-start company report (Research / Billing / Growth tabs). `userToken` is a
   * fresh Google OAuth access token (bigquery + userinfo.email) for the billing/growth
   * BigQuery query, run as the user. The call takes 2-4 minutes. */
  report: (company: string, userToken: string) =>
    api<Omit<CompanyReport, "research">>("/companies/report", {
      method: "POST",
      body: { company },
      headers: userToken ? { "X-User-Google-Token": userToken } : undefined,
    }),

  /** Hand-correct a cached profile (wrong founder name, description, etc.). */
  update: (slug: string, edits: ProfileEdits) =>
    api<CompanyProfile>(`/companies/${slug}`, { method: "PATCH", body: edits }),
};

/** Fields the user can correct on the profile card. */
export type ProfileEdits = Partial<
  Pick<
    CompanyProfile,
    | "name"
    | "website"
    | "linkedinUrl"
    | "overview"
    | "industry"
    | "hq"
    | "yearEstablished"
    | "employees"
    | "annualRevenue"
    | "founders"
  >
>;
